import { useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '@astro/api'
import { decorateConsultation } from '../consultations/consultationsData'
import { useReviewSummaries } from '../../../hooks/useReviews'
import { isExcludedAstrologer } from '../../../utils/excludedAstrologers'

function SimilarCard({ item, liveRating }) {
  // The astrologer record's own rating/reviews_count aren't kept in sync
  // upstream (verified null even for astrologers with real reviews — see
  // useReviewsAggregate) — prefer the live GET /reviews aggregate once it
  // resolves, falling back to the record's field only while it's pending.
  const rating = liveRating && !liveRating.isPending ? liveRating.averageRating : item.rating
  const reviewCount = liveRating && !liveRating.isPending ? liveRating.totalReviews : item.reviews

  return (
    <Link
      to={`/astrologers/${item.id}`}
      className="info-card overflow-hidden block h-full group hover:border-secondary/40 transition-colors"
    >
      <div className="relative h-56 w-full overflow-hidden">
        {item.image ? (
          <img
            src={item.image}
            alt={item.title}
            className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
            onError={(e) => { e.currentTarget.style.display = 'none' }}
          />
        ) : (
          <div
            className="h-full w-full flex items-center justify-center font-cinzel text-5xl font-bold"
            style={{ background: item.fallbackGradient, color: 'rgba(120,50,0,.5)' }}
          >
            {item.fallbackLetter}
          </div>
        )}
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[11px] font-bold text-[#1C140A] shadow-sm">
          ★ {reviewCount > 0 ? rating.toFixed(1) : 'New'}
        </span>
        <span className="absolute bottom-3 left-3 inline-flex items-center rounded-full bg-[#1C140A]/75 backdrop-blur px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
          {item.categoryLabel}
        </span>
      </div>
      <div className="p-5">
        <h3 className="text-base font-bold text-[#1C140A]">{item.title}</h3>
        <p className="text-xs text-[#8B7355] mt-1">{item.desc}</p>
        <div className="mt-3 flex items-center justify-between text-[11px] text-[#8B7355]">
          <span className="inline-flex items-center gap-1">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            {item.duration} min
          </span>
          <span>{reviewCount} review{reviewCount === 1 ? '' : 's'}</span>
        </div>
        <div className="astro-divider my-3" />
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-[#1C140A]">
            {item.price > 0 ? `₹${item.price.toLocaleString('en-IN')}` : 'Free'} <span className="text-[10px] font-normal text-[#8B7355]">/session</span>
          </span>
          <span className="text-xs font-semibold uppercase tracking-wide text-secondary group-hover:text-[#1C140A] transition-colors">View →</span>
        </div>
      </div>
    </Link>
  )
}

export default function SimilarAstrologersSection({ excludeId }) {
  const scrollerRef = useRef(null)

  const { data: result, isPending, isError } = useQuery({
    queryKey: ['astrologersList', 'similar'],
    queryFn: () => api.astrologers.list({ page: 1, per_page: 8 }),
  })

  const similar = useMemo(() => {
    const list = (result?.data ?? []).filter((r) => !isExcludedAstrologer(r)).map(decorateConsultation)
    return list.filter((a) => String(a.id) !== String(excludeId))
  }, [result, excludeId])

  const liveRatings = useReviewSummaries('astrologer', similar.map((a) => a.id))

  const scrollBy = (dir) => {
    scrollerRef.current?.scrollBy({ left: dir * 300, behavior: 'smooth' })
  }

  if (!isPending && !isError && similar.length === 0) return null

  return (
    <section className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 pb-20">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl font-cinzel text-[#1C140A]">Other specialists you may like</h2>
        <div className="flex items-center gap-3">
          <Link to="/consultations" className="text-sm font-semibold uppercase tracking-wide text-secondary hover:text-[#1C140A] transition-colors">
            View all →
          </Link>
          <div className="hidden sm:flex items-center gap-2">
            <button type="button" onClick={() => scrollBy(-1)} className="scroller-nav-btn" aria-label="Scroll left">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button type="button" onClick={() => scrollBy(1)} className="scroller-nav-btn" aria-label="Scroll right">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      </div>

      {isPending ? (
        <div className="similar-scroller">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="info-card overflow-hidden animate-pulse">
              <div style={{ height: 224, background: 'rgba(200,146,42,.10)' }} />
              <div className="p-5 space-y-3">
                <div className="h-4 w-2/3 rounded" style={{ background: 'rgba(200,146,42,.14)' }} />
                <div className="h-2.5 w-1/2 rounded" style={{ background: 'rgba(200,146,42,.10)' }} />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-[#8B7355]">Couldn't load other specialists right now.</p>
      ) : (
        <div ref={scrollerRef} className="similar-scroller">
          {similar.map((item) => (
            <SimilarCard key={item.id} item={item} liveRating={liveRatings[item.id]} />
          ))}
        </div>
      )}
    </section>
  )
}
